import { createContext, useContext } from 'react';
import type { AppService } from '@lg/core';
import { CurrentUser, HealthResponse, Problem } from '@lg/shared';
import type { z } from 'zod';

/** App service methods with the signed-in user already applied. */
export type UserScoped<S> = {
  [K in keyof S as K extends 'ensureSeeded' ? never : K]: S[K] extends (
    user: CurrentUser,
    ...args: infer A
  ) => infer R
    ? (...args: A) => R
    : never;
};

/** Everything the UI may ask the backend. Implemented in the browser or over HTTP. */
export interface ApiClient extends UserScoped<AppService> {
  health(): Promise<HealthResponse>;
  me(): Promise<CurrentUser>;
  /** Clears demo data and reseeds (browser mode only). */
  resetDemo?: () => void;
  /** Demo control (mock D365 only): make an operation fail once, like MOCK_D365_FAIL. */
  demoFailure?: {
    get(): Promise<string | null>;
    set(target: 'copy' | 'people' | 'createLegalEntity' | null): Promise<void>;
  };
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title);
  }
}

type GetHeaders = () => Promise<Record<string, string>>;

/**
 * HTTP client for the Azure Functions API. Only health and me exist on the
 * API so far; the rest of the endpoints follow (spec 13) and the static demo
 * uses the in-browser client meanwhile.
 */
export function createHttpApiClient(baseUrl: string, getAuthHeaders: GetHeaders): ApiClient {
  async function get<T extends z.ZodTypeAny>(path: string, schema: T, auth = true): Promise<z.infer<T>> {
    const headers = { Accept: 'application/json', ...(auth ? await getAuthHeaders() : {}) };
    const res = await fetch(`${baseUrl}${path}`, { headers });
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const parsed = Problem.safeParse(body);
      throw new ApiError(
        parsed.success ? parsed.data : { type: 'about:blank', title: res.statusText, status: res.status },
      );
    }
    return schema.parse(body);
  }
  const notYet = (): never => {
    throw new ApiError({
      type: 'about:blank',
      title: 'Not available in API mode yet',
      status: 501,
      detail: 'Run with VITE_DATA_MODE=browser until the HTTP endpoints are built.',
    });
  };
  const base = {
    health: () => get('/health', HealthResponse, false),
    me: () => get('/me', CurrentUser),
  };
  return new Proxy(base, {
    get: (target, prop: string) => (prop in target ? target[prop as keyof typeof target] : notYet),
  }) as unknown as ApiClient;
}

export const ApiContext = createContext<ApiClient | null>(null);

export function useApi(): ApiClient {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi must be used inside ApiContext');
  return api;
}
